import { initializeApp } from 'firebase/app';
import { browserSessionPersistence, getAuth, setPersistence } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { initializeFirestore } from 'firebase/firestore';
import firebaseKeys from '../keys/firebaseKeys';
import { firestoreTransportSettings } from './utils/firestoreTransport';

const env = (import.meta.env.VITE_ENV ?? 'development') as keyof typeof firebaseKeys;
const firebaseConfig = firebaseKeys[env];

const app = initializeApp(firebaseConfig, 'physiofit-services');

export const COURSE_PAYMENT_FUNCTIONS_REGION = 'europe-west1' as const;

export const functions = getFunctions(app, COURSE_PAYMENT_FUNCTIONS_REGION);
export const auth = getAuth(app);
export const db = initializeFirestore(app, firestoreTransportSettings());

/**
 * BNPL course-payment callables. Same app and region as every other webAppOnboarding callable, so
 * the signed-in onboarding session (uid === signedUpPatientId) rides along automatically.
 *
 * ERS-25: these used to run on a separate unnamed default app left over from the first BNPL
 * prototype, which no user was ever signed into — so `request.auth` was always undefined and the
 * server could not check ownership.
 */
export const getCoursePaymentFunctions = () => functions;

export const resolveFirebaseEnv = (): keyof typeof firebaseKeys => env;

/** Project the single app is actually pointed at — read from the app, never from a second key file. */
export const getResolvedFirebaseProjectId = (): string => app.options.projectId ?? '';

let webAppOnboardingAuthSessionPersistencePromise: Promise<void> | undefined;

/** Call from the web app onboarding shell before sign-in or guarded routes. Memoized so Strict Mode / remounts do not race. */
export function ensureWebAppOnboardingAuthSessionPersistence(): Promise<void> {
  webAppOnboardingAuthSessionPersistencePromise ??= setPersistence(auth, browserSessionPersistence);
  return webAppOnboardingAuthSessionPersistencePromise;
}
